import { useState } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Paper, IconButton, Box, Chip, Button } from '@mui/material';
import { GripVertical, Trash2 } from 'lucide-react';
import { LocalButtonModal } from './LocalButtonModal';
import { ButtonItem } from './interface';

const TYPE_LABELS: Record<string, string> = {
  none: 'Без дії',
  url: 'Посилання',
  web_app: 'Web App посилання',
  callback: 'Обробка у боті',
  programmed: 'Програмна кнопка',
};

function SortableItem({ item, onEdit, onDelete }: any) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  return (
    <Paper
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        p: 2,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
      }}
      onClick={() => onEdit(item)}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton {...attributes} {...listeners}>
          <GripVertical size={16} />
        </IconButton>

        <Box display="flex" alignItems="center" gap={1}>
          {item.label}

          <Chip
            label={TYPE_LABELS[item.type] || item.type}
            size="small"
            color="primary"
          />
        </Box>
      </Box>

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
      >
        <Trash2 size={16} />
      </IconButton>
    </Paper>
  );
}

export function LocalBlockButtonsTab({ value, onChange, allBlocks }: any) {
  const [items, setItems] = useState<ButtonItem[]>(value || []);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ButtonItem | null>(null);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const updated = arrayMove(items, oldIndex, newIndex);
    setItems(updated);
    onChange(updated);
  };

  const handleDelete = (id: number) => {
    const updated = items.filter((i) => i.id !== id);
    setItems(updated);
    onChange(updated);
  };

  const handleSave = (button: ButtonItem) => {
    const exists = items.some((i) => i.id === button.id);
    const updated = exists
      ? items.map((i) => (i.id === button.id ? button : i))
      : [...items, button];

    setItems(updated);
    onChange(updated);
    setEditing(null);
  };

  return (
    <>
      <Button variant="outlined" onClick={() => setModalOpen(true)}>
        Додати кнопку
      </Button>

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={items.map((i) => i.id!)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <SortableItem
              key={item.id}
              item={item}
              onEdit={(i: any) => {
                setEditing(i);
                setModalOpen(true);
              }}
              onDelete={handleDelete}
            />
          ))}
        </SortableContext>
      </DndContext>

      <LocalButtonModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
        allBlocks={allBlocks}
        editing={editing}
      />
    </>
  );
}
